import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { formatEgp } from "../lib/pricing";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;


const DURATION_ORDER = ["Monthly", "1 Year", "2 Years", "3 Years", "4 Years", "5 Years"];
const DURATION_MAP = {
  "شهري": "Monthly",
  "سنة": "1 Year",
  "سنتين": "2 Years",
  "3 سنوات": "3 Years",
  "4 سنوات": "4 Years",
  "5 سنوات": "5 Years"
};

const PlansPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDuration, setActiveDuration] = useState("Monthly");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/api/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const getDurationEn = (dur) => DURATION_MAP[dur] || dur;

  const groupedProducts = DURATION_ORDER.reduce((acc, durEn) => {
    const items = products.filter(p => getDurationEn(p.duration) === durEn);
    if (items.length > 0) acc[durEn] = items;
    return acc;
  }, {});

  const availableDurations = DURATION_ORDER.filter(d => groupedProducts[d]);
  const filteredProducts = groupedProducts[activeDuration] || [];


  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950" dir="ltr">
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Our Plans</h1>
            <p className="text-xl text-gray-300 mb-8">Choose the duration that fits your needs</p>

            <div className="inline-flex flex-wrap items-center justify-center gap-2 bg-white/5 p-2 rounded-lg">
              {availableDurations.map((dur) => (
                <button
                  key={dur}
                  onClick={() => setActiveDuration(dur)}
                  className={`px-5 py-2 rounded-md transition-all text-sm md:text-base ${
                    activeDuration === dur
                      ? "bg-red-500 text-black font-semibold"
                      : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {dur}
                  {dur !== "Monthly" && (
                    <Badge className="ml-2 bg-green-500/20 text-green-300 border-green-500/30 text-xs">
                      One-time
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </motion.div>

          {loading ? (
            <div className="text-center text-white text-xl">Loading...</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-white/5 border-white/10 hover:border-red-500/50 transition-all h-full flex flex-col">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-xl text-white">{product.name_en || product.name_ar}</CardTitle>
                        <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                          {getDurationEn(product.duration)}
                        </Badge>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">{product.description_en || product.description_ar}</p>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-white">
                          {formatEgp(product.price_total)}
                        </span>
                        <div className="text-sm text-gray-500 mt-1">
                          {getDurationEn(product.duration) === "Monthly" ? "Per month" : "One-time payment"}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <ul className="space-y-2">
                        {product.features.slice(0, 5).map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                        {product.features.length > 5 && (
                          <li className="text-red-400 text-sm">
                            +{product.features.length - 5} more features
                          </li>
                        )}
                      </ul>
                    </CardContent>
                    <CardFooter className="flex gap-2">
                      <Link to={`/plans/${product.id}`} className="flex-1">
                        <Button variant="outline" className="w-full border-white/20 text-gray-300 hover:text-white">
                          Details
                        </Button>
                      </Link>
                      <Link to={`/checkout?product=${product.id}&cycle=${product.duration === "شهري" ? "monthly" : "yearly"}`} className="flex-1">
                        <Button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-black font-semibold">
                          Buy Now
                        </Button>
                      </Link>
                    </CardFooter>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlansPage;
